import { useMemo, useState } from 'react';
import { differenceInCalendarDays, format, subDays } from 'date-fns';
import { NavArrowDown, NavArrowLeft, NavArrowRight, Search } from 'iconoir-react';
import type { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { useTheme } from 'components/core/theme-provider';
import { Skeleton } from 'components/ui/skeleton';
import { UsageBar } from 'features/usage/components/usage-bar';
import { UsageDateRangePicker } from 'features/usage/components/usage-date-range-picker';
import { UsageRangeWarning } from 'features/usage/components/usage-range-warning';
import { formatNumber } from 'features/usage/utils/usage-format';
import { useDebounce } from 'hooks/use-debounce';
import {
  type TeamsPerson,
  type TeamsPersonStatus,
  type TeamsUsageFilter,
} from '../teams-mock-data';
import { PAGE_SIZE } from '../teams-usage-paging';
import { useTeamsUsagePeople } from '../use-teams-usage-people';
import {
  teamsFilterChipActiveClassName,
  teamsFilterChipClassName,
  teamsFilterChipIdleClassName,
  teamsStatusBadgeClassName,
  teamsTableBodyRowClassName,
  teamsTableHeaderRowClassName,
  teamsTableShellClassName,
} from './teams-styles';
import { TeamsFreeToAssign } from './teams-free-to-assign';
import { TeamsUsagePanel } from './teams-usage-panel';
import { CreditsCell, PersonCell, TeamsUsageCard } from './teams-usage-card';

const SEARCH_DEBOUNCE_MS = 300;
// Same limit as the Usage page's date picker.
const MAX_RANGE_DAYS = 365;
const SKELETON_KEYS = ['skeleton-0', 'skeleton-1', 'skeleton-2', 'skeleton-3', 'skeleton-4'];
// The All / At or near a limit / Not using chips are hidden for now; set to true to show them again.
const SHOW_FILTER_CHIPS = false;

// Same shape as a loaded row, so the table keeps its height while people load.
function SkeletonRows({ borderColor }: { borderColor: string }) {
  return (
    <>
      {SKELETON_KEYS.map((key) => (
        <tr key={key} className={cn(teamsTableBodyRowClassName, borderColor)} aria-hidden>
          <td className="px-4 py-4">
            <Skeleton className="size-4" />
          </td>
          <td className="px-4 py-3.5">
            <div className="flex items-center gap-3">
              <Skeleton className="size-9 shrink-0 rounded-full" />
              <div className="flex flex-col gap-1.5">
                <Skeleton className="h-3.5 w-32" />
                <Skeleton className="h-3 w-44" />
              </div>
            </div>
          </td>
          <td className="px-4 py-4">
            <Skeleton className="h-3.5 w-28" />
          </td>
          <td className="px-4 py-4">
            <Skeleton className="h-6 w-48" />
          </td>
          <td className="px-4 py-4" />
        </tr>
      ))}
    </>
  );
}

export function TeamsUsageTab() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, SEARCH_DEBOUNCE_MS);
  const [filter, setFilter] = useState<TeamsUsageFilter>('all');
  const [pageIndex, setPageIndex] = useState(0);
  // Same default as the Usage page: the last 7 days, ending today.
  const today = useMemo(() => new Date(), []);
  const initialRange = useMemo<DateRange>(() => ({ from: subDays(today, 6), to: today }), [today]);
  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(initialRange);
  const [isRangeWarningVisible, setIsRangeWarningVisible] = useState(false);
  const [panelPersonId, setPanelPersonId] = useState<string | null>(null);

  const titleColor = theme === 'dark' ? 'text-true-white' : 'text-grey-950';
  const mutedColor = theme === 'dark' ? 'text-grey-400' : 'text-grey-500';
  const valueColor = theme === 'dark' ? 'text-true-white' : 'text-grey-900';
  const percentColor = theme === 'dark' ? 'text-grey-300' : 'text-grey-800';
  const headerColor = theme === 'dark' ? 'text-grey-300' : 'text-grey-600';
  const borderColor = theme === 'dark' ? 'border-border' : 'border-grey-100';
  const chipCountIdle = theme === 'dark' ? 'text-grey-400' : 'text-grey-500';
  const chipCountActive = 'text-grey-200';

  // While only the first day is picked, the range is that one day.
  const rangeFrom = selectedRange?.from ?? today;
  const rangeTo = selectedRange?.to ?? rangeFrom;
  const startDate = format(rangeFrom, 'yyyy-MM-dd');
  const endDate = format(rangeTo, 'yyyy-MM-dd');
  const {
    people: visible,
    counts,
    page,
    pageCount,
    totalCount,
    pageStart,
    isTruncated,
    period,
    isLoading,
    error,
  } = useTeamsUsagePeople({ startDate, endDate, filter, query: debouncedQuery, page: pageIndex });

  const handleRangeSelect = (range: DateRange | undefined) => {
    if (
      range?.from &&
      range.to &&
      differenceInCalendarDays(range.to, range.from) + 1 > MAX_RANGE_DAYS
    ) {
      setIsRangeWarningVisible(true);
      return;
    }
    setSelectedRange(range);
    setPageIndex(0);
  };
  const rangeEnd = totalCount === 0 ? 0 : pageStart + visible.length;
  const panelIndex = visible.findIndex((person) => person.id === panelPersonId);
  const panelPerson = visible[panelIndex] ?? null;

  const filters: Array<{ id: TeamsUsageFilter; labelKey: string; defaultLabel: string }> = [
    { id: 'all', labelKey: 'TEAMS_FILTER_ALL', defaultLabel: 'All' },
    {
      id: 'at_or_near_limit',
      labelKey: 'TEAMS_FILTER_AT_OR_NEAR_LIMIT',
      defaultLabel: 'At or near a limit',
    },
    { id: 'not_using', labelKey: 'TEAMS_FILTER_NOT_USING', defaultLabel: 'Not using' },
  ];

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <TeamsFreeToAssign />

      <div className="flex min-w-0 flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <label
          className={cn(
            'flex h-10 w-full max-w-80 items-center gap-2.5 rounded-card border bg-true-white px-3 dark:bg-card',
            borderColor
          )}
        >
          <Search
            width={16}
            height={16}
            strokeWidth={1.5}
            className={cn('shrink-0', mutedColor)}
            aria-hidden
          />
          <input
            type="search"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPageIndex(0);
            }}
            placeholder={t('TEAMS_SEARCH_PLACEHOLDER', {
              defaultValue: 'Find anyone by name or email',
            })}
            className={cn(
              'min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-grey-500 md:text-sm',
              valueColor
            )}
          />
        </label>

        {SHOW_FILTER_CHIPS && (
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            {filters.map((item) => {
              const isActive = filter === item.id;
              const count = counts[item.id];
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    setFilter(item.id);
                    setPageIndex(0);
                  }}
                  className={cn(
                    teamsFilterChipClassName,
                    isActive ? teamsFilterChipActiveClassName : teamsFilterChipIdleClassName
                  )}
                >
                  <span>{t(item.labelKey, { defaultValue: item.defaultLabel })}</span>
                  <span
                    className={cn(
                      'text-xs leading-heading',
                      isActive ? chipCountActive : chipCountIdle
                    )}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className={cn('text-base font-medium leading-heading', titleColor)}>
          {t('TEAMS_TOP_BY_CREDITS', {
            defaultValue: 'Top {{count}} by credits',
            count: Math.min(PAGE_SIZE, totalCount),
          })}
        </h2>

        <UsageDateRangePicker
          periodStart={period?.start}
          periodEnd={period?.end}
          selectedRange={selectedRange}
          initialFrom={initialRange.from}
          today={today}
          dayCount={differenceInCalendarDays(rangeTo, rangeFrom) + 1}
          onRangeSelect={handleRangeSelect}
          onResetRange={() => handleRangeSelect(initialRange)}
        />
      </div>

      {isRangeWarningVisible && (
        <UsageRangeWarning onDismiss={() => setIsRangeWarningVisible(false)} />
      )}

      {error && (
        <p className="text-sm leading-body text-error-high-emphasis">
          {t('TEAMS_PEOPLE_ERROR', { defaultValue: 'Could not load people. Try again.' })}
        </p>
      )}
      {!isLoading && !error && visible.length === 0 && (
        <p className={cn('text-sm leading-body', mutedColor)}>
          {t('TEAMS_PEOPLE_NO_MATCH', { defaultValue: 'No one matches that search or filter.' })}
        </p>
      )}

      {/* Mobile cards */}
      {isLoading && (
        <div className="flex flex-col gap-2 md:hidden">
          {SKELETON_KEYS.map((key) => (
            <Skeleton key={key} className="h-32 w-full rounded-section" />
          ))}
        </div>
      )}
      <ul className="flex flex-col gap-2 md:hidden">
        {!isLoading &&
          visible.map((person, index) => (
            <TeamsUsageCard
              key={person.id}
              person={person}
              rank={pageStart + index + 1}
              onOpen={() => setPanelPersonId(person.id)}
            />
          ))}
      </ul>

      {/* Desktop table */}
      <div className={cn('hidden md:block', teamsTableShellClassName)}>
        {/* overflow-x-auto turns on vertical scrolling too; a 1px rounding overflow then shows a scrollbar. */}
        <div className="overflow-x-auto overflow-y-hidden">
          <table className="w-full min-w-full border-collapse text-left table-fixed md:table-auto">
            <thead>
              <tr className={cn(teamsTableHeaderRowClassName, borderColor)}>
                <th className={cn('px-4 py-3.5 text-sm font-medium leading-body', headerColor)}>
                  #
                </th>
                <th className={cn('px-4 py-3.5 text-sm font-medium leading-body', headerColor)}>
                  {t('TEAMS_COL_PERSON', { defaultValue: 'Person' })}
                </th>
                <th className={cn('px-4 py-3.5 text-sm font-medium leading-body', headerColor)}>
                  {t('TEAMS_COL_PLAN', { defaultValue: 'Plan' })}
                </th>
                <th className={cn('px-4 py-3.5 text-sm font-medium leading-body', titleColor)}>
                  {t('TEAMS_COL_CREDITS', { defaultValue: 'Credits this month' })}
                </th>
                <th className="w-10 px-4 py-3.5" />
              </tr>
            </thead>
            <tbody>
              {isLoading && <SkeletonRows borderColor={borderColor} />}
              {!isLoading &&
                visible.map((person, index) => (
                  <tr
                    key={person.id}
                    tabIndex={0}
                    onClick={() => setPanelPersonId(person.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') setPanelPersonId(person.id);
                    }}
                    className={cn(
                      teamsTableBodyRowClassName,
                      'cursor-pointer',
                      panelPersonId === person.id && 'bg-grey-50 dark:bg-sidebar-pill-dark',
                      borderColor
                    )}
                  >
                    <td className={cn('px-4 py-4 align-middle text-sm leading-body', mutedColor)}>
                      {pageStart + index + 1}
                    </td>
                    <td className="max-w-md px-4 py-3.5 align-middle">
                      <PersonCell person={person} nameColor={titleColor} mutedColor={mutedColor} />
                    </td>
                    <td className={cn('px-4 py-4 align-middle text-sm leading-body', valueColor)}>
                      {person.plan ?? '—'}
                    </td>
                    <td className="px-4 py-4 align-middle">
                      <CreditsCell
                        person={person}
                        valueColor={valueColor}
                        mutedColor={mutedColor}
                        percentColor={percentColor}
                      />
                    </td>
                    <td className="px-4 py-4 align-middle">
                      <NavArrowRight
                        width={16}
                        height={16}
                        strokeWidth={1.5}
                        className={mutedColor}
                        aria-hidden
                      />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {isTruncated && page >= pageCount - 1 && (
        <p className={cn('text-sm leading-body', mutedColor)}>
          {t('TEAMS_USAGE_LIST_STOPS_HERE', {
            defaultValue: 'This list stops here. Use search to find anyone else.',
          })}
        </p>
      )}

      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2.5">
          <span className={cn('text-sm leading-body', headerColor)}>
            {t('TEAMS_ROWS_PER_PAGE', { defaultValue: 'Rows per page' })}
          </span>
          <button
            type="button"
            className={cn(
              'inline-flex h-9 items-center gap-1 rounded-card border bg-true-white px-2 text-sm leading-body dark:bg-card',
              borderColor,
              valueColor
            )}
          >
            {PAGE_SIZE}
            <NavArrowDown width={14} height={14} strokeWidth={1.5} className={mutedColor} />
          </button>
        </div>

        <div className="flex items-center gap-3">
          <span className={cn('text-sm leading-body', headerColor)}>
            {t('TEAMS_PAGINATION_RANGE', {
              defaultValue: '{{from}}–{{to}} of {{total}}',
              from: totalCount === 0 ? 0 : pageStart + 1,
              to: rangeEnd,
              total: totalCount,
            })}
          </span>
          <button
            type="button"
            aria-label={t('TEAMS_PREVIOUS_PAGE', { defaultValue: 'Previous page' })}
            disabled={page === 0}
            onClick={() => setPageIndex(page - 1)}
            className={cn(
              'flex size-9 items-center justify-center rounded-card border bg-true-white disabled:opacity-40 dark:bg-card',
              borderColor
            )}
          >
            <NavArrowLeft width={16} height={16} strokeWidth={1.5} className={valueColor} />
          </button>
          <button
            type="button"
            aria-label={t('TEAMS_NEXT_PAGE', { defaultValue: 'Next page' })}
            disabled={page >= pageCount - 1 || totalCount === 0}
            onClick={() => setPageIndex(page + 1)}
            className={cn(
              'flex size-9 items-center justify-center rounded-card border bg-true-white disabled:opacity-40 dark:bg-card',
              borderColor
            )}
          >
            <NavArrowRight width={16} height={16} strokeWidth={1.5} className={valueColor} />
          </button>
        </div>
      </div>

      <TeamsUsagePanel
        member={panelPerson}
        startDate={startDate}
        endDate={endDate}
        onPrevious={panelIndex > 0 ? () => setPanelPersonId(visible[panelIndex - 1].id) : undefined}
        onNext={
          panelIndex >= 0 && panelIndex < visible.length - 1
            ? () => setPanelPersonId(visible[panelIndex + 1].id)
            : undefined
        }
        onClose={() => setPanelPersonId(null)}
      />
    </div>
  );
}
